import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PostHogProvider } from "@/lib/posthog";
import ProtectedRoute from "./components/ProtectedRoute";
import RootRedirect from "./components/RootRedirect";
import { PageTracker } from "./components/PageTracker";
import "./App.css";
import ErrorBoundary from "./components/ErrorBoundary";
import RoutePathNormalizer from "./components/RoutePathNormalizer";
import AuthOriginGuard from "./components/AuthOriginGuard";

const EarningsCalculator = lazy(() => import("./pages/EarningsCalculator"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Account = lazy(() => import("./pages/Account"));
const Agency = lazy(() => import("./pages/Agency"));
const AgencyOnboarding = lazy(() => import("./pages/AgencyOnboarding"));
const NewCampaignPreview = lazy(() => import("./pages/AgencyCampaignCreationPreview"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const CreatorInvite = lazy(() => import("./pages/CreatorInvite"));
const CreatorOnboarding = lazy(() => import("./pages/CreatorOnboarding"));
const MarketerOnboarding = lazy(() => import("./pages/MarketerOnboarding"));
const Admin = lazy(() => import("./pages/Admin"));
const AgencyBaselineAdminPreview = lazy(() => import("./pages/AgencyBaselineAdminPreview"));
const AdminCreatorDetail = lazy(() => import("./pages/AdminCreatorDetail"));
const CreatorRoster = lazy(() => import("./pages/CreatorRoster"));
const CreatorDashboard = lazy(() => import("./pages/CreatorDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const VerifySuccess = lazy(() => import("./pages/VerifySuccess"));
const VerifyCancel = lazy(() => import("./pages/VerifyCancel"));
const InstantPayoutModalPlayground = lazy(() => import("./pages/InstantPayoutModalPlayground"));
const ManualScrapeFlowPlayground = lazy(() => import("./pages/ManualScrapeFlowPlayground"));
const ManualVideoSubmissionsPlayground = lazy(() => import("./pages/ManualVideoSubmissionsPlayground"));
const PublicCampaignReport = lazy(() => import("./pages/PublicCampaignReport"));
const CampaignApplications = lazy(() => import("./pages/CampaignApplications"));
const TopCreators = lazy(() => import("./components/creator/TopCreators"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen bg-zinc-900" aria-label="Loading page" />
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <PostHogProvider>
            <div className="bg-background text-foreground min-h-screen">
              <Toaster />
              <Sonner />
              <Router>
                <RoutePathNormalizer />
                <PageTracker />
                <ErrorBoundary>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/" element={<RootRedirect />} />
                      <Route path="/earnings-calculator" element={<EarningsCalculator />} />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/auth" element={<AuthOriginGuard><Auth /></AuthOriginGuard>} />
                      <Route path="/auth/callback" element={<AuthOriginGuard><AuthCallback /></AuthOriginGuard>} />
                      <Route path="/reset-password" element={<AuthOriginGuard><ResetPassword /></AuthOriginGuard>} />
                      <Route
                        path="/email-confirmation"
                        element={<EmailConfirmation />}
                      />
                      <Route
                        path="/creator-onboarding"
                        element={
                          <ProtectedRoute requiredUserType="creator">
                            <CreatorOnboarding />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/marketer-onboarding"
                        element={
                          <ProtectedRoute requiredUserType="marketer">
                            <MarketerOnboarding />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/creator/:id"
                        element={
                          <ProtectedRoute>
                            <AdminCreatorDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/campaign/:id"
                        element={
                          <ProtectedRoute>
                            <CampaignDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/*"
                        element={
                          <ProtectedRoute>
                            <Admin />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/creator-roster"
                        element={
                          <ProtectedRoute requiredUserType="marketer" allowAdminUserTypeBypass>
                            <CreatorRoster />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/invite/:creatorId"
                        element={<CreatorInvite />}
                      />
                      <Route
                        path="/report/:campaignId"
                        element={<PublicCampaignReport />}
                      />
                      <Route
                        path="/landing"
                        element={<Navigate to="/" replace />}
                      />
                      <Route
                        path="/marketplace"
                        element={
                          <ProtectedRoute
                            requiredUserType="creator"
                            allowAdminUserTypeBypass
                          >
                            <Marketplace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/creator-dashboard"
                        element={
                          <ProtectedRoute requiredUserType="creator">
                            <CreatorDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/top-creators"
                        element={
                          <ProtectedRoute allowAdminOnboardingBypass>
                            <TopCreators />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/account"
                        element={
                          <ProtectedRoute>
                            <Account />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/campaigns/new"
                        element={
                          <ProtectedRoute requiredUserType="marketer">
                            <Navigate to="/dashboard?newCampaign=1" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/campaigns/:id"
                        element={
                          <ProtectedRoute>
                            <CampaignDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/campaign-applications"
                        element={
                          <ProtectedRoute>
                            <CampaignApplications />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/verify-success" element={<VerifySuccess />} />
                      <Route path="/verify-cancel" element={<VerifyCancel />} />
                      <Route path="/agency/onboarding" element={<AgencyOnboarding />} />
                      <Route path="/agency" element={<Agency />} />
                      <Route path="/agency/creators/:creatorId" element={<Agency />} />
                      <Route path="/agency/:section" element={<Agency />} />
                      {import.meta.env.DEV && (
                        <>
                          <Route path="/__playground/agency-campaign-creation" element={<NewCampaignPreview />} />
                          <Route
                            path="/__playground/instant-payout-modal"
                            element={<InstantPayoutModalPlayground />}
                          />
                          <Route
                            path="/__playground/manual-scrape-flow"
                            element={<ManualScrapeFlowPlayground />}
                          />
                          <Route
                            path="/__playground/manual-video-submissions"
                            element={<ManualVideoSubmissionsPlayground />}
                          />
                          <Route path="/__playground/agency-baseline-admin" element={<AgencyBaselineAdminPreview />} />
                        </>
                      )}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </Router>
            </div>
          </PostHogProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
